/**
 * Zona do ambiente e hosts finais de cada serviço, abaixo do domínio do projeto
 * (consoleai#25804). No teste T0 a pessoa entendeu "domínio" como o do
 * ambiente e escreveu `dev.backofficepos…`; aqui ela vê o resultado
 * (`api.dev.backofficepos…`) enquanto digita, em vez de adivinhar o domínio base.
 *
 * Nenhuma regra de host aqui: zona, hosts e o aviso do ambiente na frente vêm
 * do servidor (`GET .../dns`, `formulario.dns`, as funções do molde). Para o
 * domínio gravado, o corpo do rascunho já traz o `dns`; só o digitado pergunta.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconAlertTriangle } from '@tabler/icons-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { lerDnsDoRascunho } from '@/api/rascunhoDeAmbiente'
import { CHAVE_PEDIDO_TERRAFORM } from '@/hooks/useTerraformChangeRequest'
import { Dado } from '../components/Dado'
import type { PassoProps } from './Passos'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }
export const PAUSA_DO_DNS_MS = 300

export function HostsDoAmbiente({ r, tenantId, rascunhoId }: PassoProps) {
  const valor = r.valor('dominio')
  const digitado = typeof valor === 'string' ? valor : ''
  const [dominio] = useDebouncedValue(digitado, PAUSA_DO_DNS_MS)
  const gravado = r.rascunho?.campos.dominio
  const igualAoGravado = dominio === (typeof gravado === 'string' ? gravado : '')
  const q = useQuery({
    queryKey: [CHAVE_PEDIDO_TERRAFORM, 'formulario-dns', tenantId, rascunhoId, r.rascunho?.versao, dominio],
    queryFn: () => lerDnsDoRascunho(tenantId, rascunhoId, dominio),
    enabled: Boolean(r.rascunho) && !igualAoGravado,
    retry: false,
    placeholderData: keepPreviousData,
  })
  const d = igualAoGravado ? r.rascunho?.dns : q.data
  if (!d) return null
  return (
    <Stack gap={6} aria-live="polite">
      {d.dominio_com_ambiente && (
        <Alert color="yellow" variant="light" role="alert"
               icon={<IconAlertTriangle size={16} stroke={1.5} />}>
          <Stack gap={6}>
            <Text size="sm" style={BARLOW}>{d.dominio_com_ambiente.o_que_dizer}</Text>
            <Group gap="xs">
              <Button size="xs" variant="default"
                      onClick={() => r.editar('dominio', d.dominio_com_ambiente!.sugerido)}>
                Usar {d.dominio_com_ambiente.sugerido}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}
      <Stack gap={2}>
        <Text size="xs" c="dimmed" style={BARLOW}>Zona do ambiente</Text>
        {d.zona ? <Dado>{d.zona}</Dado>
          : <Text size="sm" c="dimmed" style={BARLOW}>Sai quando o domínio do projeto estiver preenchido.</Text>}
      </Stack>
      {d.hosts.length > 0 && (
        <Stack gap={2}>
          <Text size="xs" c="dimmed" style={BARLOW}>Endereço de cada serviço</Text>
          {d.hosts.map((h) => (
            <Group key={h.caminho} gap={8} wrap="wrap">
              <Text size="sm" style={BARLOW}>{h.servico}</Text>
              <Dado>{h.host ?? '—'}</Dado>
              <Text size="xs" c="dimmed" style={BARLOW}>
                {h.origem === 'proposta' ? 'proposto' : 'escrito no pedido'}
              </Text>
              {h.fora_da_zona && (
                <Text size="xs" c="red" style={BARLOW}>fora da zona {d.zona}</Text>
              )}
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
